const SITE_HEADER_SELECTOR = '[data-site-header]'
const HEADER_GROUP_SELECTOR = '[class*="shopify-section-group-header-group"]'
let lastBootWidth = window.innerWidth

const getHeaderGroupSections = () => Array.from(document.querySelectorAll(HEADER_GROUP_SELECTOR))

const getSiteHeaderSection = () => {
  for (const section of getHeaderGroupSections()) {
    if (section.querySelector(SITE_HEADER_SELECTOR)) return section
  }
  for (const section of getHeaderGroupSections()) {
    if (section.querySelector('[data-lp-header]')) return section
  }
  return null
}

const getStickyNavSlot = () => getSiteHeaderSection()?.querySelector('[data-page-sticky-header-slot]')

// Replo (and similar) can hide the header group via CSS while leaving the
// theme spacer / --header-height in place. Collapse both when that happens.
// Normal pages use CSS-only heights from header.liquid — no measure/pin.
const syncHiddenHeaderSpacer = () => {
  const $headerSection = getSiteHeaderSection()
  const $spacer = document.getElementById('site-header-spacer')
  if (!$spacer) return

  const hidden = !$headerSection || getComputedStyle($headerSection).display === 'none'
  if (hidden) {
    $spacer.style.height = '0px'
    $spacer.style.minHeight = '0px'
    document.documentElement.style.setProperty('--header-height', '0px')
  } else {
    $spacer.style.height = ''
    $spacer.style.minHeight = ''
    document.documentElement.style.removeProperty('--header-height')
  }
}

function replaceHeaderAtcButton() {
  const $headerSection = getSiteHeaderSection()
  const $mainHeader = $headerSection?.querySelector('header')
  if (!$mainHeader) return

  const isSkin = $headerSection.querySelector('[data-is-skin]')?.dataset?.isSkin ?? 'false'
  const altButtonLink = document.createElement('a')
  altButtonLink.className =
    'ir-button ml-3 flex items-center justify-center !px-5 !py-2 max-xl:hidden [&[data-loading]>[data-loader]]:block [&[data-loading]>[data-text]]:invisible'

  let altButtonColorClassName = ''
  if (isSkin === 'true') {
    altButtonColorClassName = 'color-scheme-707dcb69-da00-4026-958a-05a2f47794a8 normal-case'
    altButtonLink.href = '/pages/skin-quiz'
    altButtonLink.textContent = 'TAKE SKIN QUIZ'
  } else {
    altButtonColorClassName = 'color-background-1 normal-case'
    altButtonLink.href = '/pages/hair-loss-quiz'
    altButtonLink.textContent = 'TAKE QUIZ'
  }

  altButtonLink.className = `${altButtonLink.className} ${altButtonColorClassName}`

  const existingButton = [...$mainHeader.querySelectorAll('.ir-button[data-add-to-cart]')].find(
    (btn) => !btn.closest('[data-pdp-sticky-nav-overlay]')
  )
  if (existingButton) {
    existingButton.replaceWith(altButtonLink)
  }
}

function handleStickyNavActive() {
  const $stickyNavSlot = getStickyNavSlot()
  if ($stickyNavSlot?.dataset.activeStickyNav === 'true') {
    replaceHeaderAtcButton()
  }
}

function initMegaMenuHover($mainHeader) {
  if (!$mainHeader || $mainHeader.hasAttribute('data-mega-menu-hover-init')) return
  $mainHeader.setAttribute('data-mega-menu-hover-init', '')

  const clearActiveMegaMenu = () => {
    $mainHeader.querySelectorAll('.mega-menu--open').forEach((el) => el.classList.remove('mega-menu--open'))
  }

  const setActiveMegaMenu = (menuId) => {
    clearActiveMegaMenu()
    if (!menuId) return
    const $trigger = $mainHeader.querySelector(`[data-megamenu="${menuId}"]`)
    $trigger?.closest('.megamenu-wrapper')?.classList.add('mega-menu--open')
  }

  $mainHeader.addEventListener('mouseleave', clearActiveMegaMenu)

  $mainHeader.querySelectorAll('.megamenu-wrapper, nav[data-megamenu]').forEach(($el) => {
    $el.addEventListener('mouseenter', () => {
      const menuId = $el.dataset.megamenu ?? $el.querySelector('[data-megamenu]')?.dataset.megamenu
      if (menuId) setActiveMegaMenu(menuId)
    })
  })
}

// Desktop mega menus open on focus-within. Without this, a keyboard user who
// tabs onto a trigger is forced through the entire panel before reaching the
// next opener. Escape collapses the panel and returns focus to its trigger (so
// the next Tab skips ahead); ArrowDown lets them deliberately enter the panel.
function initMegaMenuKeyboard($mainHeader) {
  if (!$mainHeader || $mainHeader.hasAttribute('data-mega-menu-kb-init')) return
  $mainHeader.setAttribute('data-mega-menu-kb-init', '')

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'

  $mainHeader.querySelectorAll('.megamenu-wrapper').forEach(($wrapper) => {
    const $trigger = $wrapper.querySelector('a[data-megamenu], button[data-megamenu]')
    const $panel = $wrapper.querySelector('nav[data-megamenu]')
    if (!$trigger || !$panel) return

    $wrapper.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        $wrapper.classList.add('mega-menu--dismissed')
        $wrapper.classList.remove('mega-menu--open')
        $trigger.focus()
        event.preventDefault()
        return
      }

      // Deliberately step into the open panel from the trigger.
      if (event.key === 'ArrowDown' && document.activeElement === $trigger) {
        $wrapper.classList.remove('mega-menu--dismissed')
        const $first = $panel.querySelector(FOCUSABLE)
        if ($first) {
          $first.focus()
          event.preventDefault()
        }
      }
    })

    // Re-arm the panel once focus actually leaves the wrapper, or on hover, so a
    // previously dismissed menu opens normally the next time around.
    $wrapper.addEventListener('focusout', (event) => {
      if (!$wrapper.contains(event.relatedTarget)) {
        $wrapper.classList.remove('mega-menu--dismissed')
      }
    })
    $wrapper.addEventListener('mouseenter', () => $wrapper.classList.remove('mega-menu--dismissed'))
  })
}

function initHeader() {
  const $header = getSiteHeaderSection()
  if (!$header || !$header.querySelector(SITE_HEADER_SELECTOR) || $header.hasAttribute('data-header-init')) return
  $header.setAttribute('data-header-init', '')
  const $mainHeader = $header.querySelector('header')

  // Mobile Menu
  const $$mobileMenuButtons = $header.querySelectorAll('button[data-mobile-menu]')
  const $mobileMenuButton = $$mobileMenuButtons[0] // Hamburger button (for class toggling)
  const $mobileMenu = $header.querySelector('nav[data-mobile-menu]')
  const $mobileMenuBackdrop = $header.querySelector('[data-mobile-menu-backdrop]')

  // Move slide panels to be direct children of the nav (outside the scroll container)
  const $$slidePanels = $mobileMenu.querySelectorAll('[data-mobile-slide-panel]')
  $$slidePanels.forEach(($panel) => {
    $mobileMenu.appendChild($panel)
  })

  const $$panelTriggers = $mobileMenu.querySelectorAll('[data-slide-panel-trigger]')

  const $mobileSearchButton = $header.querySelector('[data-header-search-open]')
  const $mobileAccountLink = $header.querySelector('[data-header-mobile-action="account"]')

  const isMobileSearchActive = () =>
    getSiteHeaderSection()?.querySelector('#layers-search-mobile-root')?.hasAttribute('data-search-active')

  const setMobileMenuToggleState = (isOpen) => {
    const $openIcon = $mobileMenuButton.querySelector('[data-mobile-menu-icon="open"]')
    const $closeIcon = $mobileMenuButton.querySelector('[data-mobile-menu-icon="close"]')

    $mobileMenuButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
    $mobileMenuButton.setAttribute(
      'aria-label',
      isOpen ? (isMobileSearchActive() ? 'Back to menu' : 'Close menu') : 'Menu'
    )
    $openIcon?.classList.toggle('hidden', isOpen)
    $closeIcon?.classList.toggle('hidden', !isOpen)
  }

  const setMobileHeaderActionState = (isMenuOpen) => {
    $mobileSearchButton?.classList.toggle('hidden', isMenuOpen)
    $mobileSearchButton?.classList.toggle('flex', !isMenuOpen)
    $mobileAccountLink?.classList.toggle('hidden', !isMenuOpen)
    $mobileAccountLink?.classList.toggle('flex', isMenuOpen)
  }

  let mobileMenuScrollY = 0
  let mobileMenuScrollLocked = false
  let mobileMenuClosingTimer = null

  const isMobileMenuOpen = () =>
    $mobileMenu &&
    !$mobileMenu.classList.contains('-translate-x-full') &&
    !$mobileMenu.classList.contains('hidden')

  const lockPageScroll = () => {
    if (mobileMenuScrollLocked) return
    mobileMenuScrollLocked = true
    mobileMenuScrollY = window.scrollY || document.documentElement.scrollTop

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = scrollbarWidth + 'px'
    }

    document.documentElement.classList.add('mobile-menu-open')
    document.body.classList.add('mobile-menu-open')
    // Body becomes position:fixed (iOS-safe scroll lock); offset it by the
    // locked scroll position so the page stays visually in place. The header
    // is already position:fixed (see connectedCallback), so it stays pinned
    // and nothing needs to be re-pinned or compensated here.
    document.body.style.top = `-${mobileMenuScrollY}px`
  }

  const unlockPageScroll = () => {
    if (!mobileMenuScrollLocked) return
    mobileMenuScrollLocked = false

    document.documentElement.classList.remove('mobile-menu-open')
    document.body.classList.remove('mobile-menu-open')
    document.body.style.paddingRight = ''
    document.body.style.top = ''

    // The body was position:fixed, so the scroll position must be restored.
    // Force an instant jump — the global `scroll-behavior: smooth` would
    // otherwise animate this, making the page visibly scroll on close.
    window.scrollTo({ top: mobileMenuScrollY, left: 0, behavior: 'instant' })
  }

  // Slide panel helpers
  const openSlidePanel = ($panel) => {
    $panel.classList.remove('invisible')
    $panel.classList.add('visible')
    requestAnimationFrame(() => {
      $panel.classList.remove('translate-x-full')
    })
  }

  const closeSlidePanel = ($panel) => {
    $panel.classList.add('translate-x-full')
    const onTransitionEnd = () => {
      $panel.classList.add('invisible')
      $panel.classList.remove('visible')
      $panel.removeEventListener('transitionend', onTransitionEnd)
    }
    $panel.addEventListener('transitionend', onTransitionEnd)
  }

  const closeAllSlidePanels = () => {
    $$slidePanels.forEach(($panel) => {
      $panel.classList.add('translate-x-full', 'invisible')
      $panel.classList.remove('visible')
    })
  }

  const closeAllAccordions = () => {
    if (!$mobileMenu) return
    $mobileMenu.querySelectorAll('[data-accordion-trigger]').forEach(($trigger) => {
      $trigger.setAttribute('aria-expanded', 'false')
      $trigger.querySelector('[data-accordion-icon]')?.classList.remove('rotate-180')
    })
    $mobileMenu.querySelectorAll('[data-accordion-panel]').forEach(($panel) => {
      $panel.classList.add('hidden')
    })
  }

  const openMobileMenu = () => {
    lockPageScroll()

    // Elevate the whole header above sticky add-to-cart bars while the drawer is open.
    // Must use !important — .site-header-section sets z-index: 50 !important on mobile.
    $header.style.setProperty('z-index', '10000', 'important')

    $mobileMenu.classList.remove('hidden')
    setTimeout(() => {
      $mobileMenu.classList.remove('-translate-x-full')
    }, 0)
    $mobileMenuBackdrop.classList.remove('pointer-events-none')
    $mobileMenuBackdrop.classList.add('opacity-40')
    setMobileMenuToggleState(true)
    setMobileHeaderActionState(true)
    trapFocus($mobileMenu, $mobileMenu, true)
  }

  const closeMobileMenu = () => {
    if (!isMobileMenuOpen() && !mobileMenuScrollLocked) return
    if (mobileMenuClosingTimer) return

    closeAllSlidePanels()
    $mobileMenu.classList.add('-translate-x-full')
    $mobileMenuBackdrop.classList.add('pointer-events-none')
    $mobileMenuBackdrop.classList.remove('opacity-40')
    setMobileMenuToggleState(false)
    setMobileHeaderActionState(false)
    removeTrapFocus()
    mobileMenuClosingTimer = setTimeout(() => {
      mobileMenuClosingTimer = null
      $mobileMenu.classList.add('hidden')
      closeAllAccordions()
      unlockPageScroll()
      // Restore the header's normal stacking once the drawer has finished
      // sliding out (it must stay elevated during the close animation).
      $header.style.removeProperty('z-index')
      if ($mobileMenu.classList.contains('-translate-x-full')) {
        document.dispatchEvent(new CustomEvent('layers:close-mobile-search'))
      }
    }, 350)
  }

  document.addEventListener('layers:mobile-search-active', () => {
    if (isMobileMenuOpen()) {
      $mobileMenuButton.setAttribute('aria-label', 'Back to menu')
    }
  })

  document.addEventListener('layers:mobile-search-inactive', () => {
    if (isMobileMenuOpen()) {
      $mobileMenuButton.setAttribute('aria-label', 'Close menu')
    }
  })

  // Add event listeners to all mobile menu buttons (hamburger and close button)
  $$mobileMenuButtons.forEach(($button) => {
    $button.addEventListener('click', () => {
      if ($mobileMenu.classList.contains('-translate-x-full')) {
        openMobileMenu()
      } else if (isMobileSearchActive()) {
        document.dispatchEvent(new CustomEvent('layers:close-mobile-search'))
      } else {
        closeMobileMenu()
      }
    })
  })

  $mobileSearchButton?.addEventListener('click', () => {
    if ($mobileMenu.classList.contains('-translate-x-full') || $mobileMenu.classList.contains('hidden')) {
      $mobileMenuButton.click()
    }
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('layers:open-mobile-search'))
    }, 100)
  })

  $mobileMenuBackdrop.addEventListener('click', () => closeMobileMenu())

  // Close the drawer (and release scroll lock) before in-drawer navigation.
  $mobileMenu.addEventListener('click', (event) => {
    const $link = event.target.closest('a[href]')
    if (!$link || $link.closest('[data-slide-panel-trigger]')) return

    const href = $link.getAttribute('href')
    if (!href || href === '#' || href.startsWith('javascript:')) return

    closeMobileMenu()
  })

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) unlockPageScroll()
  })

  // Desktop mega menus: keep open while cursor is anywhere on the header
  initMegaMenuHover($mainHeader)
  initMegaMenuKeyboard($mainHeader)

  // Slide panel triggers - open corresponding panel on click
  $$panelTriggers.forEach(($trigger) => {
    $trigger.addEventListener('click', () => {
      const panelId = $trigger.dataset.slidePanelTrigger
      const $panel = $mobileMenu.querySelector('[data-mobile-slide-panel="' + panelId + '"]')
      if ($panel) {
        openSlidePanel($panel)
      }
    })
  })

  // Slide panel back buttons - close the panel
  $mobileMenu.querySelectorAll('[data-slide-panel-back]').forEach(($btn) => {
    $btn.addEventListener('click', () => {
      const $panel = $btn.closest('[data-mobile-slide-panel]')
      if ($panel) closeSlidePanel($panel)
    })
  })

  // Slide panel close buttons - close panel + close entire mobile nav
  $mobileMenu.querySelectorAll('[data-slide-panel-close]').forEach(($btn) => {
    $btn.addEventListener('click', () => {
      closeAllSlidePanels()
      closeMobileMenu()
    })
  })

  // Accordion cards (Hair / Skin / Body) - expand/collapse in place
  const $mobileMenuScroll = $mobileMenu.querySelector('[data-mobile-menu-scroll]')

  const scrollMobileAccordionIntoView = ($trigger, $panel) => {
    if (!$mobileMenuScroll || !$trigger || !$panel) return

    // Wait for layout after un-hiding the panel before measuring.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const containerRect = $mobileMenuScroll.getBoundingClientRect()
        const triggerRect = $trigger.getBoundingClientRect()
        const panelRect = $panel.getBoundingClientRect()
        const scrollTop = $mobileMenuScroll.scrollTop
        const clientHeight = $mobileMenuScroll.clientHeight
        const maxScrollTop = $mobileMenuScroll.scrollHeight - clientHeight

        const triggerTop = triggerRect.top - containerRect.top + scrollTop
        const triggerBottom = triggerTop + triggerRect.height
        const panelBottom = panelRect.bottom - containerRect.top + scrollTop

        // Show as much panel content as possible while keeping the accordion
        // header fully inside the scroll area.
        const minScrollForHeader = Math.max(0, triggerBottom - clientHeight)
        let targetScrollTop = Math.min(triggerTop, Math.max(minScrollForHeader, panelBottom - clientHeight))
        targetScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop))

        if (Math.abs(scrollTop - targetScrollTop) > 1) {
          $mobileMenuScroll.scrollTop = targetScrollTop
        }
      })
    })
  }

  const $$accordionTriggers = $mobileMenu.querySelectorAll('[data-accordion-trigger]')
  $$accordionTriggers.forEach(($trigger) => {
    $trigger.addEventListener('click', () => {
      const panelId = $trigger.dataset.accordionTrigger
      const $panel = $mobileMenu.querySelector('[data-accordion-panel="' + panelId + '"]')
      const $icon = $trigger.querySelector('[data-accordion-icon]')
      const isOpen = $trigger.getAttribute('aria-expanded') === 'true'

      // Close any other open accordion first (single-open behavior)
      $$accordionTriggers.forEach(($other) => {
        if ($other === $trigger) return
        $other.setAttribute('aria-expanded', 'false')
        const $otherPanel = $mobileMenu.querySelector('[data-accordion-panel="' + $other.dataset.accordionTrigger + '"]')
        const $otherIcon = $other.querySelector('[data-accordion-icon]')
        if ($otherPanel) $otherPanel.classList.add('hidden')
        if ($otherIcon) $otherIcon.classList.remove('rotate-180')
      })

      $trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true')
      if ($panel) $panel.classList.toggle('hidden', isOpen)
      if ($icon) $icon.classList.toggle('rotate-180', !isOpen)

      if (!isOpen && $panel) {
        scrollMobileAccordionIntoView($trigger, $panel)
      }
    })
  })

  window.matchMedia('(max-width: 1023px)').addEventListener('change', (e) => {
    if (e && !e.matches && isMobileMenuOpen()) {
      closeMobileMenu()
    }
  })

  handleStickyNavActive()
}

function teardownHeader() {
  document.querySelectorAll('[data-header-init]').forEach((el) => el.removeAttribute('data-header-init'))
  document.querySelectorAll('[data-mega-menu-hover-init]').forEach((el) => el.removeAttribute('data-mega-menu-hover-init'))
  document.querySelectorAll('[data-mega-menu-kb-init]').forEach((el) => el.removeAttribute('data-mega-menu-kb-init'))
}

function boot() {
  // defer: runs after the document (including Replo hide-header CSS) is parsed.
  syncHiddenHeaderSpacer()
  initHeader()
}

document.addEventListener('site-header:sticky-nav-active', handleStickyNavActive)

boot()

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.matches?.(HEADER_GROUP_SELECTOR)) {
      teardownHeader()
      boot()
    }
  })
  document.addEventListener('shopify:section:unload', () => boot())
}

window.addEventListener('load', () => {
  syncHiddenHeaderSpacer()

  if (window.location.hash.length > 0) {
    const $el = document.querySelector(window.location.hash)
    if ($el) {
      window.scrollTo({ top: $el.offsetTop - 100, behavior: 'smooth' })
    }
  }
})

let resizeTicking = false
window.addEventListener('resize', () => {
  releaseStuckScrollLock()
  if (resizeTicking) return
  resizeTicking = true
  requestAnimationFrame(() => {
    resizeTicking = false
    // Re-init header chrome when crossing layout breakpoints (drawer, mega menu).
    // Heights are CSS-owned — no remeasure.
    if (window.innerWidth !== lastBootWidth) {
      lastBootWidth = window.innerWidth
      boot()
    }
  })
})

// Safety net: the page scroll lock (`mobile-menu-open` → body position:fixed,
// shared CSS) is released by closure state inside initHeader. If the header
// re-initializes while the drawer is open — e.g. crossing the mobile↔desktop
// breakpoint with the drawer open — that closure state is lost and the lock
// can stick. Force-release it from module scope whenever we're on desktop and
// detect a leftover lock, independent of any closure.
const DESKTOP_MQ = window.matchMedia('(min-width: 1024px)')
function releaseStuckScrollLock() {
  if (!DESKTOP_MQ.matches) return
  const locked =
    document.body.classList.contains('mobile-menu-open') || document.body.style.position === 'fixed'
  if (!locked) return

  const storedTop = Math.abs(parseInt(document.body.style.top || '0', 10)) || 0
  document.documentElement.classList.remove('mobile-menu-open')
  document.body.classList.remove('mobile-menu-open', 'overflow-hidden')
  document.body.style.position = ''
  document.body.style.top = ''
  document.body.style.paddingRight = ''
  if (storedTop) window.scrollTo({ top: storedTop, left: 0, behavior: 'instant' })

  // Collapse any open drawer DOM so it doesn't linger visually.
  document
    .querySelectorAll('nav[data-mobile-menu]')
    .forEach(($m) => $m.classList.add('-translate-x-full', 'hidden'))
}
DESKTOP_MQ.addEventListener('change', releaseStuckScrollLock)
